import {
  Component,
  DoCheck,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation
} from '@angular/core';

import { cloneDeep, defaults, isEqual } from 'lodash';

import { PaginationConfig } from './pagination-config';
import { PaginationEvent } from './pagination-event';

/**
 * Component for rendering pagination
 *
 * Usage:
 * <code><pre>
 * // Individual module import
 * import { PaginationModule } from 'patternfly-ng/pagination';
 * // Or
 * import { PaginationModule } from 'patternfly-ng';
 *
 * // NGX Bootstrap
 * import { BsDropdownConfig, BsDropdownModule } from 'ngx-bootstrap/dropdown';
 *
 * &#64;NgModule({
 *   imports: [PaginationModule, BsDropdownModule.forRoot(),...],
 *   providers: [BsDropdownConfig]
 * })
 * export class AppModule(){}
 * </pre></code>
 *
 * Optional:
 * <code><pre>
 * import { PaginationConfig, PaginationEvent } from 'patternfly-ng/pagination';
 * </pre></code>
 */

export class PaginationComponent implements DoCheck, OnInit {
  /**
   * The Pagination config contaning component properties
   */
   config: PaginationConfig;

  /**
   * The Event is emitted when Page Size is changed
   */
   onPageSizeChange = new EventEmitter();

  /**
   * The Event is emitted when Page Number is Changed
   */
   onPageNumberChange = new EventEmitter();

  private defaultConfig = {
    pageNumber: 1,
    pageSizeIncrements: [5, 10, 20, 40, 80, 100],
    pageSize: 5
  } as PaginationConfig;
  private _pageNumber: number;
  private prevConfig: PaginationConfig;
  private _lastPageNumber: number;

  /**
   * The default constructor
   */
  constructor() {}

  // Initialization

  /**
   * Setup component configuration upon initialization
   */
  ngOnInit(): void {
    this.setupConfig();
    this.lastPageNumber = this.getLastPageNumber();
  }

  /**
   * Check if the component config has changed
   */
  ngDoCheck(): void {
    // Do a deep compare on config
    if (!isEqual(this.config, this.prevConfig)) {
      this.setupConfig();
    }
  }

  /**
   * Setup default config
   */
  protected setupConfig(): void {
    if (this.config !== undefined) {
      defaults(this.config, this.defaultConfig);
    } else {
      this.config = cloneDeep(this.defaultConfig);
    }
    this.lastPageNumber = this.getLastPageNumber();
    this.pageNumber = this.config.pageNumber;
    this.prevConfig = cloneDeep(this.config);
  }

  // Accessors

  get pageNumber(): number {
    return (this.config.totalItems !== undefined && this.config.totalItems > 0) ? this._pageNumber : 0;
  }

  set pageNumber(pageNumber: number) {
    this._pageNumber = pageNumber;
  }

  /**
   * Return last page number
   */
  get lastPageNumber(): number {
    return (this.config.totalItems !== undefined && this.config.totalItems > 0) ? this._lastPageNumber : 0;
  }

  /**
   * Update Last page Number
   */
  set lastPageNumber(value: number) {
    this._lastPageNumber = value;
  }

  // Actions

  /**
   * Jump to First Page
   */
  gotoFirstPage(): void {
    if (this.config.pageNumber !== 1) {
      this.updatePageNumber(1);
    }
  }

  /**
   * Go to Previous Page
   */
  gotoPreviousPage(): void {
    if (this.config.pageNumber !== 1) {
      this.updatePageNumber(this.config.pageNumber - 1);
    }
  }

  /**
   * Go to Next Page
   */
  gotoNextPage(): void {
    if (this.config.pageNumber < this.lastPageNumber) {
      this.updatePageNumber(this.config.pageNumber + 1);
    }
  }

  /**
   * Jump to Last Page
   */
  gotoLastPage(): void {
    if (this.config.pageNumber < this.lastPageNumber) {
      this.updatePageNumber(this.lastPageNumber);
    }
  }

  /**
   * Return start index and end index of current page
   */
  getCurrentPage() {
    return this.getStartIndex() + ' - ' + this.getEndIndex();
  }

  /**
   * Start Index of Current Page
   */
  protected getStartIndex(): number {
    return (this.config.totalItems !== undefined && this.config.totalItems > 0)
      ? this.config.pageSize * (this.config.pageNumber - 1) + 1 : 0;
  }

  /**
   * End Index of Current Page
   */
  protected getEndIndex(): number {
    let numFullPages = Math.floor(this.config.totalItems / this.config.pageSize);
    let numItemsOnLastPage = this.config.totalItems - (numFullPages * this.config.pageSize) || this.config.pageSize;
    let numItemsOnPage = this.isLastPage() ? numItemsOnLastPage : this.config.pageSize;
    return (this.config.totalItems !== undefined && this.config.totalItems > 0)
      ? (this.getStartIndex() + numItemsOnPage - 1) : 0;
  }

  /**
   * Page number is changed via input field's focus event
   */
  onPageNumberBlur($event: FocusEvent) {
    let newPageNumber: number = parseInt(String(this.pageNumber), 10);
    if (isNaN(newPageNumber)) {
      newPageNumber = this.pageNumber = this.config.pageNumber;
    }

    if (newPageNumber > this.lastPageNumber) {
      this.updatePageNumber(this.lastPageNumber);
    } else if (newPageNumber < 1) {
      this.updatePageNumber(1);
    } else {
      this.updatePageNumber(newPageNumber);
    }
  }

  /**
   * Page number is changed via input field's keyboard event
   */
  onPageNumberKeyup($event: KeyboardEvent): void {
    let keycode = $event.keyCode ? $event.keyCode : $event.which;
    if (keycode === 13) {
      this.onPageNumberBlur(null);
    }
  }

  // Private

  /**
   * Page size is changed
   * @param newPageSize new page size
   */
  private onPageSizeUpdate($event: Event, newPageSize: number): void {
    this.config.pageSize = newPageSize;
    this.lastPageNumber = this.getLastPageNumber();
    this.gotoFirstPage();
    this.onPageSizeChange.emit({
      pageSize: newPageSize
    } as PaginationEvent);
  }

  /**
   * Update the Page Number
   * @param newPageNumber new page number
   */
  private updatePageNumber(newPageNumber: number): void {
    this.config.pageNumber = this.pageNumber = newPageNumber;
    this.onPageNumberChange.emit({
      pageNumber: newPageNumber
    } as PaginationEvent);
  }

  /**
   * Get Last Page Number
   */
  private getLastPageNumber(): number {
    return Math.ceil(this.config.totalItems / this.config.pageSize);
  }

  /**
   * Check if current Page is Last Page
   */
  private isLastPage(): boolean {
    return (this.config.pageNumber === this.lastPageNumber);
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  encapsulation: ViewEncapsulation.None,
  selector: 'pfng-pagination',
  template: `<form class="content-view-pf-pagination list-view-pf-pagination clearfix"><div class="form-group"><div class="padding-right-10"><div class="btn-group dropdown" dropdown><button #pageSizeMenu type="button" class="btn btn-default dropdown-toggle" dropdownToggle>{{config.pageSize}}<span class="caret"></span></button><ul class="dropdown-menu" *dropdownMenu><li class="display-length-increment" [ngClass]="{'selected': increment === config?.pageSize}" *ngFor="let increment of config?.pageSizeIncrements"><a role="menuitem" (click)="onPageSizeUpdate($event, increment)">{{increment}}</a></li></ul></div></div><span for="pageSizeMenu" class="per-page-label">per page</span></div><div class="form-group"><span><span class="pagination-pf-item-current">{{getCurrentPage()}}</span>&nbsp;of&nbsp; <span class="pagination-pf-items-total">{{config.totalItems}}</span></span><ul class="pagination pagination-pf-back"><li [ngClass]="{'disabled': config.pageNumber === 1}"><a class="goto-first-page" title="First Page" (click)="gotoFirstPage()"><span class="i fa fa-angle-double-left"></span></a></li><li [ngClass]="{'disabled': config.pageNumber === 1}"><a class="goto-prev-page" title="Previous Page" (click)="gotoPreviousPage()"><span class="i fa fa-angle-left"></span></a></li></ul><input class="pagination-pf-page" name="pageNumber" type="text" [(ngModel)]="pageNumber" (blur)="onPageNumberBlur($event)" (keyup.enter)="onPageNumberKeyup($event)"> <span>of&nbsp;<span class="pagination-pf-pages">{{lastPageNumber}}</span></span><ul class="pagination pagination-pf-forward"><li [ngClass]="{'disabled': config.pageNumber === lastPageNumber}"><a class="goto-next-page" title="Next Page" (click)="gotoNextPage()"><span class="i fa fa-angle-right"></span></a></li><li [ngClass]="{'disabled': config.pageNumber === lastPageNumber}"><a class="goto-last-page" title="Last Page" (click)="gotoLastPage()"><span class="i fa fa-angle-double-right"></span></a></li></ul></div></form>`
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'config': [{ type: Input },],
'onPageSizeChange': [{ type: Output, args: ['onPageSizeChange', ] },],
'onPageNumberChange': [{ type: Output, args: ['onPageNumberChange', ] },],
};
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
